import React, { useState, useEffect, useRef, ReactElement, useCallback } from 'react'

export interface TruncateTextProps {
  renderItem: (isTruncated: boolean) => ReactElement | null
  tooltipText?: string
  tooltip?: boolean
  wrapperType?: 'truncate' | 'tooltip' | 'none'
  parentRef?: React.RefObject<HTMLElement | null>
  tooltipPosition?: string
}

export default function TruncateText({
  renderItem,
  tooltipText,
  wrapperType = 'tooltip',
  parentRef,
  tooltipPosition = 'tooltip-top',
}: TruncateTextProps) {
  const [isTruncated, setIsTruncated] = useState<boolean>(false)
  const measurableElementRef = useRef<HTMLDivElement>(null)

  const checkIfTruncated = useCallback(() => {
    if (measurableElementRef.current) {
      const { offsetWidth, scrollWidth } = measurableElementRef.current

      const currentlyTruncated = scrollWidth > offsetWidth

      setIsTruncated((prev) => (prev === currentlyTruncated ? prev : currentlyTruncated))
    } else {
      setIsTruncated(false)
    }
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(checkIfTruncated, 0)

    let observer: ResizeObserver | undefined
    const targetElement = parentRef?.current ?? measurableElementRef.current

    if (targetElement) {
      observer = new ResizeObserver(checkIfTruncated)
      observer.observe(targetElement)
    }

    return () => {
      clearTimeout(timeoutId)
      if (observer && targetElement) {
        observer.unobserve(targetElement)
      }
      observer?.disconnect()
    }
  }, [checkIfTruncated, parentRef])

  if (wrapperType === 'truncate') {
    return (
      <div
        data-testid="truncate-tooltip-wrapper"
        className={`w-full z-50  ${isTruncated ? `tooltip tooltip-${tooltipPosition || 'top'}` : ''}`}
        data-tip={isTruncated ? tooltipText : undefined}
      >
        <div ref={measurableElementRef} className="truncate block w-full">
          {renderItem(isTruncated)}
        </div>
      </div>
    )
  }

  if (wrapperType === 'tooltip') {
    const contentElement = renderItem(false)
    return (
      <div
        data-testid="tooltip-always"
        data-tip={tooltipText}
        className={` flex text-left justify-start items-center tooltip z-99  tooltip-${tooltipPosition || ''}`}
      >
        <div className="tooltip-content text-neutral-content bg-neutral font-light text-left">{tooltipText}</div>
        {contentElement}
      </div>
    )
  }

  return renderItem(false)
}
